/**
 * S9 — concept detail pane: description, relationships, attributes.
 */

import React from 'react';
import { useLanguage } from '@/state/language/LanguageProvider';
import type {
  AiOntologyAttribute,
  AiOntologyConcept,
  AiOntologyRelationship,
} from '@/types/aiStudioAgent';

interface ConceptDetailProps {
  concept: AiOntologyConcept | null;
  relationships: AiOntologyRelationship[];
  /** Map concept id → code for relationship arrow labels. */
  conceptCodeById: Map<string, string>;
  attributes: AiOntologyAttribute[];
  attributesLoading: boolean;
  attributesErrorKey: string | null;
}

export const ConceptDetail: React.FC<ConceptDetailProps> = ({
  concept,
  relationships,
  conceptCodeById,
  attributes,
  attributesLoading,
  attributesErrorKey,
}) => {
  const { t } = useLanguage();

  if (!concept) {
    return (
      <p className="text-base text-[var(--color-surface-text-secondary)]">
        {t('aiStudio.ontology.selectConcept')}
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <header>
        <h2 className="text-lg font-semibold text-[var(--color-surface-text)]">
          {concept.code}
        </h2>
        <p className="text-base text-[var(--color-surface-text-secondary)]">
          {concept.name}
        </p>
      </header>

      <dl className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <div>
          <dt className="text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.ontology.kind')}
          </dt>
          <dd className="text-base text-[var(--color-surface-text-secondary)]">
            {concept.conceptKind}
          </dd>
        </div>
        <div>
          <dt className="text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.ontology.scope')}
          </dt>
          <dd className="text-base text-[var(--color-surface-text-secondary)]">
            {concept.isPlatform
              ? t('aiStudio.ontology.scopePlatform')
              : t('aiStudio.ontology.scopeTenant')}
          </dd>
        </div>
        <div>
          <dt className="text-base font-medium text-[var(--color-surface-text)]">
            {t('aiStudio.ontology.status')}
          </dt>
          <dd className="text-base text-[var(--color-surface-text-secondary)]">
            {concept.lifecycleStatus}
          </dd>
        </div>
      </dl>

      <section>
        <h3 className="mb-1 text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.ontology.description')}
        </h3>
        <p className="whitespace-pre-wrap text-base text-[var(--color-surface-text-secondary)]">
          {concept.description?.trim()
            ? concept.description
            : t('aiStudio.ontology.noDescription')}
        </p>
      </section>

      <section>
        <h3 className="mb-2 text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.ontology.relationships')}
        </h3>
        {relationships.length === 0 ? (
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.ontology.noRelationships')}
          </p>
        ) : (
          <ul className="space-y-2">
            {relationships.map((rel) => {
              const fromCode =
                conceptCodeById.get(rel.fromConceptId) ?? rel.fromConceptId.slice(0, 8);
              const toCode =
                conceptCodeById.get(rel.toConceptId) ?? rel.toConceptId.slice(0, 8);
              return (
                <li
                  key={rel.id}
                  className="rounded border border-[var(--color-border)] bg-[var(--color-secondary-bg)] px-3 py-2 text-base text-[var(--color-surface-text)]"
                >
                  <p className="font-medium">
                    {rel.code}
                    <span className="font-normal text-[var(--color-surface-text-secondary)]">
                      {' '}
                      · {fromCode} → {toCode}
                    </span>
                  </p>
                  {rel.description?.trim() && (
                    <p className="mt-1 text-[var(--color-surface-text-secondary)]">
                      {rel.description}
                    </p>
                  )}
                  <p className="mt-1 text-[var(--color-surface-text-secondary)]">
                    {rel.cardinality}
                  </p>
                </li>
              );
            })}
          </ul>
        )}
      </section>

      <section>
        <h3 className="mb-2 text-base font-semibold text-[var(--color-surface-text)]">
          {t('aiStudio.ontology.attributes')}
        </h3>
        {attributesLoading ? (
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.ontology.loading')}
          </p>
        ) : attributesErrorKey ? (
          <p role="alert" className="text-base text-[var(--color-error)]">
            {t(attributesErrorKey)}
          </p>
        ) : attributes.length === 0 ? (
          <p className="text-base text-[var(--color-surface-text-secondary)]">
            {t('aiStudio.ontology.noAttributes')}
          </p>
        ) : (
          <ul className="space-y-2">
            {attributes.map((attr) => (
              <li
                key={attr.id}
                className="rounded border border-[var(--color-border)] px-3 py-2 text-base text-[var(--color-surface-text)]"
              >
                <p className="font-medium">
                  {attr.code}
                  <span className="font-normal text-[var(--color-surface-text-secondary)]">
                    {' '}
                    · {attr.dataType}
                  </span>
                  {attr.isRequired && (
                    <span className="ml-2 text-[var(--color-info)]">
                      {t('aiStudio.ontology.required')}
                    </span>
                  )}
                </p>
                <p className="text-[var(--color-surface-text-secondary)]">{attr.name}</p>
                {attr.description?.trim() && (
                  <p className="mt-1 text-[var(--color-surface-text-secondary)]">
                    {attr.description}
                  </p>
                )}
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
};
